// @gqlflow
import gql from "graphql-tag";

import type {GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import {extractProgram} from "../internal/extractors.ts";

import type {
    listTopProgramSpinoffs,
    listTopProgramSpinoffsVariables,
} from "./__graphql-types__/listTopProgramSpinoffs.ts";
import type {
    FetchParams,
    ProjectListFetcher,
    ProjectListResponse,
} from "./project-list-fetcher-types.ts";
import type {GqlSpinoffOrigin} from "./user-spinoffs-of-program-fetcher.ts";

const LIST_TOP_PROGRAM_SPINOFFS_QUERY: GqlOperation<
    listTopProgramSpinoffs,
    listTopProgramSpinoffsVariables
> = gqlOp(gql`
    query listTopProgramSpinoffs(
        $pageInfo: ListProgramsPageInfo
        $from: SpinoffOrigin!
        $sort: ListProgramSortOrder
    ) {
        listTopProgramSpinoffs(pageInfo: $pageInfo, from: $from, sort: $sort) {
            complete
            cursor
            programs {
                id
                key
                authorKaid
                authorNickname
                displayableSpinoffCount
                imagePath
                sumVotesIncremented
                translatedTitle: title
                url
            }
        }
    }
`);
export type GqlSpinoffOriginKind =
    | "CHALLENGE"
    | "INTERACTIVE"
    | "PROJECT"
    | "TALKTHROUGH"
    | "PROGRAM";

export type ConstructorParams = {
    gqlFetch: GqlFetchFn;
    spinoffOrigin: GqlSpinoffOrigin;
    itemsPerPage: number;
};
export class TopSpinoffProjectsFetcher implements ProjectListFetcher {
    gqlFetch: GqlFetchFn;
    spinoffOrigin: GqlSpinoffOrigin;
    itemsPerPage: number;

    constructor(params: ConstructorParams) {
        this.gqlFetch = params.gqlFetch;
        this.spinoffOrigin = params.spinoffOrigin;
        this.itemsPerPage = params.itemsPerPage;
    }

    async fetch(params: FetchParams): Promise<ProjectListResponse> {
        const response: listTopProgramSpinoffs = await this.gqlFetch(
            LIST_TOP_PROGRAM_SPINOFFS_QUERY,
            {
                variables: {
                    sort: params.sort,
                    from: this.spinoffOrigin,
                    pageInfo: {
                        cursor: params.cursor,
                        itemsPerPage: this.itemsPerPage,
                    },
                },
            },
        );

        const programs = throwIfNull(response.listTopProgramSpinoffs);
        return {
            complete: programs.complete,
            cursor: programs.cursor,
            scratchpads: programs.programs.map(extractProgram),
        };
    }

    fingerprint(): string {
        return (
            "TopSpinoffProjectsFetcher " + this.spinoffOrigin.programOrContentId
        );
    }
}

function throwIfNull<T>(data?: T | null): T {
    if (data == null) {
        throw new KAError(
            "Received null data from the server",
            Errors.Internal,
        );
    }
    return data;
}
